/**
 * Marketplace page - browse + install connectors.
 *
 * Mirrors FOCTTA Core's integration-list-page.tsx layout but uses
 * Discovery's design tokens + lucide icons. Sections:
 *
 *   1. Connected (top) - installed connectors with status badges
 *   2. Category chip filter + search input
 *   3. Template grid grouped by category
 *   4. Install dialog (modal) - renders JSONSchema config form
 *
 * Backend contract:
 *   GET    /v1/foundational/integrations/templates
 *   GET    /v1/foundational/integrations/connectors
 *   POST   /v1/foundational/integrations/connectors
 *   POST   /v1/foundational/integrations/connectors/:id/test
 *   POST   /v1/foundational/integrations/connectors/:id/{activate,pause,disable}
 *   DELETE /v1/foundational/integrations/connectors/:id
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Cloud,
  Copy,
  ExternalLink,
  Filter,
  Loader2,
  Lock,
  MapPin,
  Network,
  PlugZap,
  Search,
  Server,
  Shield,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';

import {
  CATEGORY_META,
  type ConnectorCategory,
  type ConnectorInstance,
  type ConnectorTemplate,
  installConnector,
  listConnectors,
  listTemplates,
} from '@/lib/marketplace-api';
import { getOutboundIps, type OutboundIpsResponse } from '@/lib/api';
import { GlassCard, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

interface InstallDialogState {
  open: boolean;
  template?: ConnectorTemplate;
  displayName: string;
  config: Record<string, string>;
  eventSubscriptions: string[];
  submitting: boolean;
  error: string | null;
}

export function MarketplacePage(): JSX.Element {
  const [templates, setTemplates] = useState<ConnectorTemplate[]>([]);
  const [connectors, setConnectors] = useState<ConnectorInstance[]>([]);
  const [outboundIps, setOutboundIps] = useState<OutboundIpsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ConnectorCategory | 'all'>('all');
  // OBS-4 (2026-05-26) - read ?q= from URL so deep-links from
  // /discovery/connectors (e.g. /admin/integrations?q=postgres) actually
  // pre-filter the marketplace. Previously the search state was
  // initialized to '' regardless of URL; the deep-link looked like it
  // should work but silently dropped the filter.
  const routerState = useRouterState();
  const initialSearch = (() => {
    try {
      const sp = new URLSearchParams(routerState.location.search);
      return sp.get('q') ?? '';
    } catch {
      return '';
    }
  })();
  const [search, setSearch] = useState(initialSearch);
  const [installDialog, setInstallDialog] = useState<InstallDialogState>({
    open: false,
    displayName: '',
    config: {},
    eventSubscriptions: [],
    submitting: false,
    error: null,
  });

  useEffect(() => {
    void reloadData();
    // Outbound IPs in parallel - best-effort; non-fatal.
    void getOutboundIps()
      .then((ips) => setOutboundIps(ips))
      .catch(() => undefined);
  }, []);

  async function reloadData(): Promise<void> {
    setLoading(true);
    setLoadError(null);
    try {
      const [tpl, inst] = await Promise.all([
        listTemplates({ limit: 200 }),
        listConnectors(),
      ]);
      setTemplates(tpl.templates);
      setConnectors(inst.connectors);
    } catch (err) {
      setLoadError(String((err as Error)?.message ?? err));
    } finally {
      setLoading(false);
    }
  }

  async function copyOutboundCidrs(): Promise<void> {
    if (!outboundIps) return;
    try {
      await navigator.clipboard.writeText(outboundIps.entries.map((e) => e.cidr).join(', '));
    } catch {
      // permission denied - operator can copy manually
    }
  }

  const filteredTemplates = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
      if (!t.enabled) return false;
      if (lowerSearch) {
        const haystack = `${t.displayName} ${t.description ?? ''} ${t.platformKey}`.toLowerCase();
        if (!haystack.includes(lowerSearch)) return false;
      }
      return true;
    });
  }, [templates, categoryFilter, search]);

  const templatesByCategory = useMemo(() => {
    const map = new Map<ConnectorCategory, ConnectorTemplate[]>();
    for (const t of filteredTemplates) {
      const arr = map.get(t.category) ?? [];
      arr.push(t);
      map.set(t.category, arr);
    }
    return map;
  }, [filteredTemplates]);

  const totalByCategory = useMemo(() => {
    const m = new Map<ConnectorCategory | 'all', number>();
    m.set('all', templates.filter((t) => t.enabled).length);
    for (const t of templates) {
      if (!t.enabled) continue;
      m.set(t.category, (m.get(t.category) ?? 0) + 1);
    }
    return m;
  }, [templates]);

  function openInstall(template: ConnectorTemplate): void {
    const schema = template.configSchema as { properties?: Record<string, { default?: unknown }> };
    const defaults: Record<string, string> = {};
    if (schema.properties) {
      for (const [k, v] of Object.entries(schema.properties)) {
        if (v && typeof v === 'object' && 'default' in v && v.default !== undefined) {
          defaults[k] = String(v.default);
        }
      }
    }
    setInstallDialog({
      open: true,
      template,
      displayName: template.displayName,
      config: defaults,
      eventSubscriptions: [...template.supportedEvents],
      submitting: false,
      error: null,
    });
  }

  function closeInstall(): void {
    setInstallDialog((s) => ({ ...s, open: false }));
  }

  async function submitInstall(): Promise<void> {
    if (!installDialog.template) return;
    setInstallDialog((s) => ({ ...s, submitting: true, error: null }));
    try {
      await installConnector({
        templatePlatformKey: installDialog.template.platformKey,
        displayName: installDialog.displayName,
        config: installDialog.config,
        eventSubscriptions: installDialog.eventSubscriptions,
      });
      closeInstall();
      await reloadData();
    } catch (err) {
      setInstallDialog((s) => ({
        ...s,
        submitting: false,
        error: String((err as Error)?.message ?? err),
      }));
    }
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      <header className="mb-6">
        <div className="flex items-center gap-3 text-3xl font-semibold tracking-tight text-foreground">
          <PlugZap className="h-7 w-7 text-primary" />
          Marketplace
        </div>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Browse + install connectors to wire FOCTTA Discovery to your existing tools.
          Identity, SIEM, ITSM, DSPM, communication, cloud, India Stack - same generic outbound
          pipeline + 18 vendor-specific inbound parsers.
        </p>
      </header>

      {/* G6.5 - Registry navigation strip + outbound-IPs surface.
          The platform has TWO integration catalogues:
          External Tools (this page) and Scanner Pods (edge-fleet).
          Surface both peer-level here + answer the firewall-team
          "what IPs?" question without a doc lookup. */}
      <section className="mb-8 grid gap-3 sm:grid-cols-1 lg:grid-cols-3">
        <div className="rounded-lg border border-primary/30 bg-card/40 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
            <PlugZap className="h-4 w-4 text-primary" />
            External Tools (this page)
          </div>
          <p className="text-xs text-muted-foreground">
            Outbound connectors - SIEMs, ITSM, Communication, DSPM-ingest, IAM, Cloud, India Stack.
            38+ vendors. <strong className="text-foreground">You are here.</strong>
          </p>
        </div>

        <Link
          to="/admin/edge-fleet"
          className="group rounded-lg border border-border bg-card/60 p-4 transition hover:border-primary/50 hover:bg-card"
        >
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
            <Server className="h-4 w-4 text-primary" />
            Scanner Pods
            <ExternalLink className="ml-auto h-3 w-3 opacity-60 group-hover:opacity-100" />
          </div>
          <p className="text-xs text-muted-foreground">
            Customer-VPC pods - Oracle / MSSQL / S3 / SMB / BigQuery / pgaudit / CloudTrail.
            ~24 scanner kinds.
          </p>
        </Link>

        <div className="rounded-lg border border-emerald-500/30 bg-card/40 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
            <Network className="h-4 w-4 text-emerald-500" />
            Outbound IPs
            <span className="ml-auto rounded bg-muted/50 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              {outboundIps ? outboundIps.revision : '…'}
            </span>
          </div>
          {outboundIps ? (
            <>
              <ul className="mb-2 space-y-0.5 font-mono text-[11px] text-muted-foreground">
                {outboundIps.entries.map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-2">
                    <span className="truncate">{e.cidr}</span>
                    <span className="shrink-0 rounded bg-muted/50 px-1 py-0.5 text-[9px]">
                      {e.region}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={copyOutboundCidrs}
                className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-primary/50"
              >
                <Copy className="h-3 w-3" /> Copy CIDRs
              </button>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> fetching…
            </p>
          )}
        </div>
      </section>

      {/* Connected section */}
      <section className="mb-10">
        <div className="mb-3 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Connected{' '}
            <span className="font-mono text-xs text-foreground/60">({connectors.length})</span>
          </h2>
        </div>
        {connectors.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/30 px-6 py-8 text-center text-sm text-muted-foreground">
            No connectors installed yet. Browse the marketplace below to add one.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {connectors.map((c) => (
              <Link
                key={c.id}
                to="/admin/integrations/connector/$connectorId"
                params={{ connectorId: c.id }}
                className="group rounded-lg border border-border bg-card/60 px-4 py-3 transition hover:border-primary/50 hover:bg-card"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {c.displayName}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {c.templatePlatformKey}
                    </div>
                  </div>
                  <StatusBadge status={c.status} health={c.healthStatus} />
                </div>
                {c.lastSuccessAt && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Last success: {new Date(c.lastSuccessAt).toLocaleString()}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Search + category chips */}
      <section className="mb-8">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates by name, vendor, or platform key…"
              className="w-full rounded-lg border border-border bg-background py-2 pl-10 pr-3 text-sm shadow-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Filter className="h-4 w-4" />
            <span>{filteredTemplates.length} of {templates.length} templates</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <CategoryChip
            label="All"
            count={totalByCategory.get('all') ?? 0}
            active={categoryFilter === 'all'}
            onClick={() => setCategoryFilter('all')}
          />
          {CATEGORY_META.map((cat) => {
            const count = totalByCategory.get(cat.key);
            if (!count) return null;
            return (
              <CategoryChip
                key={cat.key}
                label={cat.label}
                count={count}
                active={categoryFilter === cat.key}
                onClick={() => setCategoryFilter(cat.key)}
              />
            );
          })}
        </div>
      </section>

      {/* Template grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading marketplace…
        </div>
      ) : loadError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
          <AlertTriangle className="mb-2 h-5 w-5" />
          Failed to load marketplace: {loadError}
        </div>
      ) : (
        <section className="space-y-10">
          {CATEGORY_META.map((cat) => {
            const items = templatesByCategory.get(cat.key);
            if (!items || items.length === 0) return null;
            return (
              <div key={cat.key}>
                <div className="mb-3 flex items-baseline justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{cat.label}</h3>
                    <p className="text-xs text-muted-foreground">{cat.description}</p>
                  </div>
                  <span className="font-mono text-xs text-foreground/40">{items.length}</span>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((t) => (
                    <TemplateCard key={t.id} template={t} onInstall={() => openInstall(t)} />
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {installDialog.open && installDialog.template && (
        <InstallDialog
          state={installDialog}
          onClose={closeInstall}
          onChange={(patch) => setInstallDialog((s) => ({ ...s, ...patch }))}
          onSubmit={submitInstall}
        />
      )}
    </div>
  );
}

function CategoryChip(props: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition',
        props.active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-card/60 text-foreground hover:bg-card hover:border-primary/50',
      )}
    >
      {props.label}
      <span className={cn('font-mono text-[10px]', props.active ? 'text-primary-foreground/80' : 'text-foreground/40')}>
        {props.count}
      </span>
    </button>
  );
}

function StatusBadge(props: {
  status: ConnectorInstance['status'];
  health: ConnectorInstance['healthStatus'];
}): JSX.Element {
  if (props.status === 'active' && props.health === 'healthy') {
    return <Badge variant="default" className="bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/15">Active</Badge>;
  }
  if (props.status === 'active' && props.health === 'degraded') {
    return <Badge variant="default" className="bg-amber-500/15 text-amber-500 hover:bg-amber-500/15">Degraded</Badge>;
  }
  if (props.status === 'error' || props.health === 'failing') {
    return <Badge variant="default" className="bg-red-500/15 text-red-500 hover:bg-red-500/15">Error</Badge>;
  }
  if (props.status === 'paused') return <Badge variant="secondary">Paused</Badge>;
  if (props.status === 'disabled') return <Badge variant="outline">Disabled</Badge>;
  if (props.status === 'testing') return <Badge variant="default" className="bg-blue-500/15 text-blue-500 hover:bg-blue-500/15">Testing</Badge>;
  return <Badge variant="outline">Draft</Badge>;
}

function TemplateCard(props: {
  template: ConnectorTemplate;
  onInstall: () => void;
}): JSX.Element {
  const t = props.template;
  return (
    <GlassCard className="flex h-full flex-col p-5 hover:border-primary/30 transition">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="truncate text-sm font-semibold text-foreground">{t.displayName}</h4>
            <Badge variant="outline" className="text-[10px] uppercase">
              {t.tier}
            </Badge>
          </div>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
            {t.description ?? 'No description.'}
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <Tag icon={<Lock className="h-3 w-3" />}>{t.authType}</Tag>
        <Tag icon={t.handlesPii ? <Shield className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />} tone={t.handlesPii ? 'amber' : 'emerald'}>
          {t.handlesPii ? 'Handles PII' : 'No PII'}
        </Tag>
        {t.dataResidency === 'in_region' && (
          <Tag icon={<MapPin className="h-3 w-3" />} tone="cyan">In-region</Tag>
        )}
        {t.privacyRiskLevel === 'high' && (
          <Tag icon={<AlertTriangle className="h-3 w-3" />} tone="red">High risk</Tag>
        )}
        {t.privacyRiskLevel === 'medium' && (
          <Tag icon={<AlertTriangle className="h-3 w-3" />} tone="amber">Medium risk</Tag>
        )}
        {t.privacyRiskLevel === 'low' && (
          <Tag icon={<CheckCircle2 className="h-3 w-3" />} tone="emerald">Low risk</Tag>
        )}
        {t.isIndiaStack && (
          <Tag icon={<Sparkles className="h-3 w-3" />} tone="violet">India Stack</Tag>
        )}
        {t.supportsErasure && (
          <Tag icon={<Zap className="h-3 w-3" />} tone="cyan">Erasure</Tag>
        )}
      </div>

      <div className="mt-3 text-xs text-muted-foreground">
        <Activity className="-mt-0.5 mr-1 inline h-3 w-3" />
        {t.supportedEvents.length} outbound + {t.inboundEvents.length} inbound events
      </div>

      <div className="mt-auto pt-4">
        <Button onClick={props.onInstall} className="w-full" size="sm">
          Install
        </Button>
      </div>
    </GlassCard>
  );
}

function Tag(props: {
  icon: React.ReactNode;
  children: React.ReactNode;
  tone?: 'emerald' | 'amber' | 'cyan' | 'red' | 'violet' | 'default';
}): JSX.Element {
  const tone = props.tone ?? 'default';
  const cls: Record<string, string> = {
    default: 'bg-muted text-foreground/70',
    emerald: 'bg-emerald-500/10 text-emerald-500',
    amber: 'bg-amber-500/10 text-amber-500',
    cyan: 'bg-cyan-500/10 text-cyan-500',
    red: 'bg-red-500/10 text-red-500',
    violet: 'bg-violet-500/10 text-violet-500',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium',
        cls[tone],
      )}
    >
      {props.icon}
      {props.children}
    </span>
  );
}

interface InstallDialogProps {
  state: InstallDialogState;
  onClose: () => void;
  onChange: (patch: Partial<InstallDialogState>) => void;
  onSubmit: () => void;
}

function InstallDialog(props: InstallDialogProps): JSX.Element {
  const s = props.state;
  const tpl = s.template!;
  const schema = tpl.configSchema as {
    properties?: Record<string, { type?: string; format?: string; title?: string; description?: string; enum?: string[]; default?: unknown }>;
    required?: string[];
  };
  const required = new Set(schema.required ?? []);
  const fields = Object.entries(schema.properties ?? {});

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={props.onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-xl border border-border bg-background p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Install
            </div>
            <h3 className="mt-0.5 text-xl font-semibold text-foreground">{tpl.displayName}</h3>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              {tpl.description ?? ''}
            </p>
          </div>
          <button
            onClick={props.onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 space-y-1.5">
          <label className="text-xs font-medium text-foreground/80">Display name</label>
          <input
            type="text"
            value={s.displayName}
            onChange={(e) => props.onChange({ displayName: e.target.value })}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {fields.length > 0 && (
          <div className="mb-4 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Configuration
            </div>
            {fields.map(([key, spec]) => (
              <div key={key} className="space-y-1.5">
                <label className="text-xs font-medium text-foreground/80">
                  {spec.title ?? key}
                  {required.has(key) && <span className="ml-1 text-red-500">*</span>}
                </label>
                {spec.description && (
                  <p className="text-[11px] text-muted-foreground">{spec.description}</p>
                )}
                {spec.enum ? (
                  <select
                    value={s.config[key] ?? ''}
                    onChange={(e) => props.onChange({ config: { ...s.config, [key]: e.target.value } })}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select…</option>
                    {spec.enum.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={spec.format === 'password' ? 'password' : spec.format === 'uri' ? 'url' : 'text'}
                    value={s.config[key] ?? ''}
                    onChange={(e) => props.onChange({ config: { ...s.config, [key]: e.target.value } })}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={spec.format === 'password' ? '••••••••' : `Enter ${key}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {tpl.supportedEvents.length > 0 && (
          <div className="mb-4 space-y-1.5">
            <label className="text-xs font-medium text-foreground/80">
              Event subscriptions
            </label>
            <p className="text-[11px] text-muted-foreground">
              {tpl.supportedEvents.length} available events. All selected by default.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {tpl.supportedEvents.map((ev) => (
                <button
                  key={ev}
                  type="button"
                  onClick={() => {
                    const has = s.eventSubscriptions.includes(ev);
                    props.onChange({
                      eventSubscriptions: has
                        ? s.eventSubscriptions.filter((e) => e !== ev)
                        : [...s.eventSubscriptions, ev],
                    });
                  }}
                  className={cn(
                    'rounded-full px-2.5 py-1 font-mono text-[10px] transition',
                    s.eventSubscriptions.includes(ev)
                      ? 'bg-primary/15 text-primary'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70',
                  )}
                >
                  {ev}
                </button>
              ))}
            </div>
          </div>
        )}

        {s.error && (
          <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            <AlertTriangle className="-mt-0.5 mr-1 inline h-4 w-4" />
            {s.error}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={props.onClose} disabled={s.submitting}>
            Cancel
          </Button>
          <Button onClick={props.onSubmit} disabled={s.submitting}>
            {s.submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Installing…
              </>
            ) : (
              'Install'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
